// ============================================================================
// Profile Sync Utility
// Syncs data from compliance forms back to user profile
// This allows forms to be the "source of truth" for employee data
// ============================================================================

import { db } from './db';

/**
 * Updates user profile with data from compliance form submissions
 * @param {string} userId - The user ID
 * @param {string} formType - Type of form ('I-9', 'W-4', 'DE-4', 'WTPN')
 * @param {object} formData - The form data submitted
 */
export async function syncProfileFromForm(userId, formType, formData) {
  if (!userId || !formData) return;

  try {
    const user = await db.users.get(userId);
    if (!user) return;

    const currentProfile = user.profile || {};
    let updatedProfile = { ...currentProfile };
    let hasChanges = false;

    switch (formType) {
      case 'I-9':
        // I-9 has the most comprehensive personal data
        if (formData.firstName && formData.firstName !== currentProfile.firstName) {
          updatedProfile.firstName = formData.firstName;
          hasChanges = true;
        }
        if (formData.middleName !== undefined) {
          updatedProfile.middleName = formData.middleName;
          hasChanges = true;
        }
        if (formData.lastName && formData.lastName !== currentProfile.lastName) {
          updatedProfile.lastName = formData.lastName;
          hasChanges = true;
        }
        if (formData.dateOfBirth && formData.dateOfBirth !== currentProfile.dateOfBirth) {
          updatedProfile.dateOfBirth = formData.dateOfBirth;
          hasChanges = true;
        }
        // SSN - store only last 4 for display (masked)
        if (formData.ssn) {
          const ssnClean = formData.ssn.replace(/\D/g, '');
          if (ssnClean.length >= 4) {
            updatedProfile.ssnLast4 = ssnClean.slice(-4);
            // Store encrypted full SSN if encryption is available
            // In production, use proper encryption
            updatedProfile.ssnEncrypted = btoa(ssnClean); // Simple encoding for demo
            hasChanges = true;
          }
        }
        // Address from I-9
        if (formData.address || formData.streetAddress) {
          const newAddress = {
            address1: formData.address || formData.streetAddress || '',
            address2: formData.aptNumber || '',
            city: formData.city || '',
            state: formData.state || '',
            zip: formData.zipCode || formData.zip || '',
          };
          if (!currentProfile.address?.mailing ||
            newAddress.address1 !== currentProfile.address?.mailing?.address1 ||
            newAddress.city !== currentProfile.address?.mailing?.city) {
            updatedProfile.address = {
              ...currentProfile.address,
              mailing: newAddress,
            };
            hasChanges = true;
          }
        }
        // Email and phone from I-9
        if (formData.email && formData.email !== user.email) {
          // Don't change login email, but store as contact email
          updatedProfile.contactEmail = formData.email;
          hasChanges = true;
        }
        if (formData.phone && formData.phone !== currentProfile.phone) {
          updatedProfile.phone = formData.phone;
          hasChanges = true;
        }
        break;

      case 'W-4':
        // W-4 has name, address, and SSN
        if (formData.firstName && formData.firstName !== currentProfile.firstName) {
          updatedProfile.firstName = formData.firstName;
          hasChanges = true;
        }
        if (formData.lastName && formData.lastName !== currentProfile.lastName) {
          updatedProfile.lastName = formData.lastName;
          hasChanges = true;
        }
        // Address from W-4
        if (formData.address) {
          const newAddress = {
            address1: formData.address || '',
            address2: '',
            city: formData.city || '',
            state: formData.state || '',
            zip: formData.zipCode || formData.zip || '',
          };
          if (!currentProfile.address?.mailing ||
            newAddress.address1 !== currentProfile.address?.mailing?.address1) {
            updatedProfile.address = {
              ...currentProfile.address,
              mailing: newAddress,
            };
            hasChanges = true;
          }
        }
        // SSN from W-4
        if (formData.ssn) {
          const ssnClean = formData.ssn.replace(/\D/g, '');
          if (ssnClean.length >= 4) {
            updatedProfile.ssnLast4 = ssnClean.slice(-4);
            updatedProfile.ssnEncrypted = btoa(ssnClean);
            hasChanges = true;
          }
        }
        // Tax filing status and W-4 details
        if (formData.filingStatus) {
          updatedProfile.taxFilingStatus = formData.filingStatus;

          // Sync detailed W-4 data to taxWithholding object
          updatedProfile.taxWithholding = {
            ...currentProfile.taxWithholding,
            federal: {
              filingStatus: formData.filingStatus,
              multipleJobs: formData.step2?.twoJobsOnly || false,
              childrenCount: parseInt(formData.step3?.childrenUnder17 || 0),
              otherDependentsCount: parseInt(formData.step3?.otherDependents || 0),
              dependentCredit: (parseInt(formData.step3?.childrenUnder17 || 0) * 2000) + (parseInt(formData.step3?.otherDependents || 0) * 500),
              otherIncome: parseFloat(formData.step4?.otherIncome || 0),
              deductions: parseFloat(formData.step4?.deductions || 0),
              extraWithholding: parseFloat(formData.step4?.extraWithholding || 0),
              lastUpdated: new Date().toISOString()
            }
          };
          hasChanges = true;
        }
        break;

      case 'DE-4':
        // DE-4 is similar to W-4 but California specific
        if (formData.firstName && formData.firstName !== currentProfile.firstName) {
          updatedProfile.firstName = formData.firstName;
          hasChanges = true;
        }
        if (formData.lastName && formData.lastName !== currentProfile.lastName) {
          updatedProfile.lastName = formData.lastName;
          hasChanges = true;
        }
        // California state tax info
        if (formData.filingStatus) {
          updatedProfile.stateFilingStatus = formData.filingStatus;

          // Sync detailed DE-4 data to taxWithholding object
          updatedProfile.taxWithholding = {
            ...currentProfile.taxWithholding,
            state: {
              state: 'CA',
              filingStatus: formData.filingStatus,
              regularAllowances: parseInt(formData.regularAllowances || 0),
              additionalAllowances: parseInt(formData.additionalAllowances || 0),
              additionalWithholding: parseFloat(formData.additionalWithholding || 0),
              exempt: formData.exempt || false,
              lastUpdated: new Date().toISOString()
            }
          };
          hasChanges = true;
        }
        break;

      case 'WTPN':
        // Wage Theft Prevention Notice has employment details
        if (formData.employeeHireDate || formData.hireDate) {
          updatedProfile.hireDate = formData.employeeHireDate || formData.hireDate;

          // Sync employment details
          updatedProfile.employmentDetails = {
            ...currentProfile.employmentDetails,
            rateOfPay: parseFloat(formData.rateOfPay || 0),
            payBasis: formData.payBasis || 'hourly',
            overtimeRate: parseFloat(formData.overtimeRate || 0),
            payFrequency: formData.payPeriod || 'biweekly',
            payDay: formData.payDay || '',
            workersComp: {
              carrier: formData.workersCompCarrier || '',
              policy: formData.workersCompPolicy || '',
              phone: formData.workersCompPhone || ''
            },
            lastUpdated: new Date().toISOString()
          };
          hasChanges = true;
        }
        break;

      default:
        break;
    }

    // Update the user profile if there are changes
    if (hasChanges) {
      const now = Date.now();
      await db.users.update(userId, {
        profile: updatedProfile,
        updatedAt: now,
        // Update name if first/last name changed
        ...(updatedProfile.firstName && updatedProfile.lastName ? {
          name: `${updatedProfile.firstName} ${updatedProfile.lastName}`
        } : {}),
      });

      // Log the sync
      await db.complianceAuditLog.add({
        id: `sync_${Date.now()}`,
        action: 'profile_synced_from_form',
        timestamp: new Date().toISOString(),
        userId,
        documentType: formType,
        description: `Profile updated from ${formType} form submission`,
      });

      console.log(`Profile synced from ${formType} form for user ${userId}`);
    }

    return updatedProfile;
  } catch (error) {
    console.error('Error syncing profile from form:', error);
  }
}

/**
 * Gets existing profile data formatted for use in compliance forms
 * @param {object} user - The user object
 * @returns {object} - Formatted data for forms
 */
export function getProfileDataForForms(user) {
  if (!user) return {};

  const profile = user.profile || {};
  const address = profile.address?.mailing || {};

  return {
    // Personal info
    firstName: profile.firstName || user.name?.split(' ')[0] || '',
    middleName: profile.middleName || '',
    lastName: profile.lastName || user.name?.split(' ').slice(1).join(' ') || '',
    dateOfBirth: profile.dateOfBirth || '',

    // Contact
    email: user.email || profile.contactEmail || '',
    phone: profile.phone || '',

    // Address
    address: address.address1 || '',
    streetAddress: address.address1 || '',
    aptNumber: address.address2 || '',
    city: address.city || '',
    state: address.state || '',
    zipCode: address.zip || '',
    zip: address.zip || '',

    // Employment
    hireDate: profile.hireDate || '',
    startDate: profile.hireDate || '',
    employeeHireDate: profile.hireDate || '',

    // Existing filing status if available
    filingStatus: profile.taxFilingStatus || 'single',
    stateFilingStatus: profile.stateFilingStatus || 'single',
  };
}

/**
 * Check if profile has minimum required data
 * @param {object} user - The user object
 * @returns {object} - Validation result with missing fields
 */
export function validateProfileCompleteness(user) {
  const profile = user?.profile || {};
  const missing = [];

  if (!profile.firstName) missing.push('First Name');
  if (!profile.lastName) missing.push('Last Name');
  if (!profile.phone) missing.push('Phone Number');
  if (!profile.address?.mailing?.address1) missing.push('Address');
  if (!profile.emergencyContacts?.length) missing.push('Emergency Contact');

  return {
    isComplete: missing.length === 0,
    completeness: Math.round(((5 - missing.length) / 5) * 100),
    missingFields: missing,
  };
}
